import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Info, ArrowRight } from 'lucide-react';
import { PageHero, PillLink } from '@/components/ui-bits';
import { Reveal } from '@/components/motion';
import { OrnamentWave } from '@/components/Ornaments';
import { ROUTE_SLUGS } from '@/i18n';

export default function HowItWorks() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const steps = t('how.steps', { returnObjects: true });
  const p = (key) => { const s = ROUTE_SLUGS[key][lang]; return s ? `/${lang}/${s}` : `/${lang}`; };

  return (
    <div data-testid="how-page">
      <PageHero
        eyebrow={t('nav.how')}
        title={t('how.title')}
        subtitle={t('how.subtitle')}
        ornament={<OrnamentWave className="hidden md:block absolute right-10 top-24 w-64 text-terracotta/25" />}
      />

      {/* Note */}
      <section className="px-5 md:px-10 pb-8">
        <div className="max-w-[1280px] mx-auto">
          <Reveal>
            <div data-testid="how-note" className="rounded-3xl bg-salvia/20 border border-salvia/40 p-8 md:p-12 flex gap-6 items-start">
              <span className="shrink-0 w-14 h-14 rounded-2xl bg-salvia/30 text-salvia flex items-center justify-center"><Info size={26} /></span>
              <div>
                <h2 className="font-serif text-3xl text-textDark mb-3">{t('how.noteTitle')}</h2>
                <p className="text-xl text-textDark/75">{t('how.noteText')}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Steps timeline */}
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1080px] mx-auto">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-textDark mb-16">{t('how.stepsTitle')}</h2></Reveal>
          <div className="relative">
            <div className="absolute left-[27px] md:left-[39px] top-4 bottom-4 w-px bg-terracotta/25" aria-hidden="true" />
            <div className="flex flex-col gap-12 md:gap-16">
              {steps.map((s, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div data-testid={`how-step-${i}`} className="relative flex gap-6 md:gap-10 items-start">
                    <div className="shrink-0 relative z-10 w-14 h-14 md:w-20 md:h-20 rounded-full bg-terracotta text-beige flex items-center justify-center font-serif text-2xl md:text-3xl">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="pt-1 md:pt-4">
                      <h3 className="font-serif text-2xl md:text-3xl text-textDark mb-2">{s.t}</h3>
                      <p className="text-lg md:text-xl text-textDark/70 max-w-xl">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="mt-16"><PillLink to={p('contact')} testId="how-cta">{t('common.cta_contact')} <ArrowRight size={20} /></PillLink></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
